import React, { useCallback, useEffect, useState } from "react";
import { Button, Chip, CircularProgress, Dialog, IconButton, TextField, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SendIcon from "@material-ui/icons/Send";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { fmtDateTimeShort } from "../../translate/localeHelper";
import whatsBackground from "../../assets/wa-background.webp";
import { MAX_TEXT } from "./supportDraft";

const GREEN = "#25D366";
const TEAL = "#128C7E";

const STATUS_COLOR = {
  recibida: { bg: "#e3f2fd", fg: "#0d47a1" },
  necesita_datos: { bg: "#fff3cd", fg: "#7a5b00" },
  en_revision: { bg: "#ede7f6", fg: "#4527a0" },
  borrador_listo: { bg: "#ede7f6", fg: "#4527a0" },
  borde: { bg: "#ede7f6", fg: "#4527a0" },
  pasada_checklist: { bg: "#ede7f6", fg: "#4527a0" },
  respondida: { bg: "#d9f5e5", fg: "#0b6b3a" },
  resuelta_checklist: { bg: "#d9f5e5", fg: "#0b6b3a" },
  cerrada: { bg: "#eceff1", fg: "#455a64" },
};

const useStyles = makeStyles(theme => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    background: theme.palette.background.paper,
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    cursor: "pointer",
    flexWrap: "wrap",
    "&:focus-visible": { outline: `2px solid ${TEAL}` },
  },
  id: { fontWeight: 600, color: TEAL },
  preview: {
    flex: "1 1 160px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  date: { fontSize: 12, color: theme.palette.text.secondary },
  chip: { fontWeight: 600, height: 22, fontSize: 12 },
  expand: { transition: "transform .2s" },
  expandOpen: { transform: "rotate(180deg)" },
  body: {
    padding: theme.spacing(1.5),
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#0b141a" : "#efeae2",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  row: { display: "flex" },
  rowMine: { justifyContent: "flex-end" },
  bubble: {
    maxWidth: "85%",
    padding: "6px 10px 4px",
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    wordBreak: "break-word",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
  },
  mine: {
    background: theme.palette.type === "dark" ? "#005c4b" : "#dcf8c6",
    borderRadius: "8px 0 8px 8px",
  },
  ours: {
    background: theme.palette.type === "dark" ? "#202c33" : "#ffffff",
    borderRadius: "0 8px 8px 8px",
  },
  clarify: {
    background: theme.palette.type === "dark" ? "#3b3320" : "#fff8e1",
    borderRadius: "0 8px 8px 8px",
    borderLeft: "3px solid #f9a825",
  },
  label: { fontSize: 12, fontWeight: 700, marginBottom: 2 },
  text: { fontSize: 14, lineHeight: 1.4, whiteSpace: "pre-wrap" },
  time: { fontSize: 11, textAlign: "right", opacity: 0.65, marginTop: 2 },
  thumbs: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 },
  thumb: {
    width: 84,
    height: 84,
    objectFit: "cover",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "zoom-in",
    background: "#fff",
  },
  replyBox: { padding: theme.spacing(1.5), borderTop: `1px solid ${theme.palette.divider}` },
  replyActions: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 },
  sendBtn: { background: TEAL, color: "#fff", "&:hover": { background: "#0e6e63" } },
  closed: { padding: theme.spacing(1, 1.5), fontSize: 13, color: theme.palette.text.secondary },
  zoomImg: { maxWidth: "100%", maxHeight: "85vh", display: "block", margin: "auto" },
}));

const fmt = d =>
  fmtDateTimeShort(d, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

// Las imágenes se piden con axios (lleva el JWT); un <img src> directo no.
const AttachmentThumb = ({ id, onOpen, classes }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    api
      .get(`/support/attachments/${id}`, { responseType: "blob" })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        if (alive) setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);
  if (!url) return <CircularProgress size={20} />;
  return (
    <img
      src={url}
      alt={i18n.t("support.openImage")}
      className={classes.thumb}
      tabIndex={0}
      role="button"
      onClick={() => onOpen(url)}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && onOpen(url)}
    />
  );
};

const SupportThread = ({ request, defaultOpen, refreshKey, onChanged }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(!!defaultOpen);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [zoom, setZoom] = useState(null);
  // feat_410 — marca de leído: al abrir el hilo se avisa al backend (una vez por respuesta nueva).
  const [seenLocal, setSeenLocal] = useState(false);
  const isNew = !!request.unread && !seenLocal;

  // una consulta recién creada se abre sola aunque el hilo ya estuviera montado
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/support/requests/${request.id}`);
      setDetail(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [request.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  useEffect(() => {
    if (!open || !request.unread || seenLocal) return;
    setSeenLocal(true);
    api
      .post(`/support/requests/${request.id}/seen`)
      .then(() => window.dispatchEvent(new Event("support:seen")))
      .catch(() => setSeenLocal(false));
  }, [open, request.unread, request.id, seenLocal]);

  // llegó otra respuesta después de haber leído: vuelve a contar como nueva
  useEffect(() => {
    if (!request.unread) setSeenLocal(false);
  }, [request.unread, request.updatedAt]);

  const status = detail?.request?.status || request.status;
  const col = STATUS_COLOR[status] || STATUS_COLOR.recibida;
  const messages = detail?.messages || [];
  const attachments = detail?.attachments || [];
  const last = messages[messages.length - 1];
  const waitingReply = status === "necesita_datos" && last?.role === "ia_repregunta";

  const copyId = async e => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(request.id));
      toast.info(i18n.t("support.idCopied"));
    } catch (_) {
      toast.warn(i18n.t("support.idCopyFailed"));
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.post(`/support/requests/${request.id}/messages`, { text });
      setReply("");
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    }
    setSending(false);
  };

  const toggle = () => setOpen(o => !o);

  return (
    <div className={classes.card}>
      <div
        className={classes.head}
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle())}
      >
        <span className={classes.id}>#{request.id}</span>
        <Tooltip title={i18n.t("support.copyId")}>
          <IconButton size="small" onClick={copyId} aria-label={i18n.t("support.copyId")}>
            <FileCopyOutlinedIcon style={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Chip
          size="small"
          className={classes.chip}
          style={{ background: col.bg, color: col.fg }}
          label={i18n.t(`support.status.${status}`)}
        />
        {isNew && (
          <Chip size="small" className={classes.chip} style={{ background: "#d32f2f", color: "#fff" }} label={i18n.t("support.newReply")} data-support-new="1" />
        )}
        <span className={classes.preview}>{request.text}</span>
        <span className={classes.date}>{fmt(request.createdAt)}</span>
        <ExpandMoreIcon className={`${classes.expand} ${open ? classes.expandOpen : ""}`} fontSize="small" />
      </div>
      {open && (
        <>
          <div className={classes.body}>
            {loading && !detail && <CircularProgress size={22} style={{ alignSelf: "center" }} />}
            {messages.map(m => {
              const mine = m.role === "cliente";
              const cls = mine ? classes.mine : m.role === "ia_repregunta" ? classes.clarify : classes.ours;
              const label = mine
                ? i18n.t("support.bubbleMine")
                : m.role === "ia_repregunta"
                ? i18n.t("support.bubbleClarify")
                : i18n.t("support.bubbleOurs");
              const atts = attachments.filter(a => a.messageId === m.id);
              return (
                <div key={m.id} className={`${classes.row} ${mine ? classes.rowMine : ""}`}>
                  <div className={`${classes.bubble} ${cls}`}>
                    <div className={classes.label} style={{ color: mine ? "#075e54" : TEAL }}>
                      {label}
                    </div>
                    <div className={classes.text}>{m.body}</div>
                    {atts.length > 0 && (
                      <div className={classes.thumbs}>
                        {atts.map(a => (
                          <AttachmentThumb key={a.id} id={a.id} classes={classes} onOpen={setZoom} />
                        ))}
                      </div>
                    )}
                    <div className={classes.time}>{fmt(m.createdAt)}</div>
                  </div>
                </div>
              );
            })}
            {/* adjuntos sin mensaje asociado (no debería pasar, pero no se pierden) */}
            {attachments.some(a => !a.messageId) && (
              <div className={classes.thumbs}>
                {attachments
                  .filter(a => !a.messageId)
                  .map(a => (
                    <AttachmentThumb key={a.id} id={a.id} classes={classes} onOpen={setZoom} />
                  ))}
              </div>
            )}
          </div>
          {status === "cerrada" ? (
            <div className={classes.closed}>{i18n.t("support.closedNote")}</div>
          ) : (
            <div className={classes.replyBox}>
              {waitingReply && <div className={classes.closed} style={{ padding: "0 0 6px" }}>{i18n.t("support.replyHelp")}</div>}
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rowsMax={5}
                value={reply}
                onChange={e => setReply(e.target.value.slice(0, MAX_TEXT))}
                placeholder={i18n.t("support.replyPlaceholder")}
                inputProps={{ "aria-label": i18n.t("support.replyPlaceholder") }}
              />
              <div className={classes.replyActions}>
                <span className={classes.date}>
                  {i18n.t("support.counter", { count: reply.length, max: MAX_TEXT })}
                </span>
                <Button
                  size="small"
                  variant="contained"
                  className={classes.sendBtn}
                  disabled={!reply.trim() || sending}
                  endIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
                  onClick={sendReply}
                >
                  {i18n.t("support.replySend")}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="lg">
        {zoom && (
          <img
            src={zoom}
            alt=""
            className={classes.zoomImg}
            onClick={() => setZoom(null)}
            title={i18n.t("support.closeImage")}
          />
        )}
      </Dialog>
    </div>
  );
};

export default SupportThread;
